import { useState, useContext, useEffect } from 'react';
import { readUrlState, hasDeepLink, writeUrlState } from './utils/deepLink';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider, ThemeContext } from './context/ThemeContext';
import { CategoryProvider, useCategory } from './context/CategoryContext';
import { MonthProvider, useMonth } from './context/MonthContext';
import { useMetaData } from './hooks/useApi';
import { fmtMonth } from './utils/theme';
import { isCountryAllowed } from './utils/access';
import LoginPage from './components/LoginPage';
import AdminView from './views/AdminView';
import LandingView from './views/LandingView';
import Sidebar from './components/Sidebar';
import CategoryBehaviourView from './views/CategoryBehaviourView';
import {
  PenetrationGeoView, PenetrationAgeView, PenetrationGenderView,
  BrandPenetrationGeoView, BrandPenetrationAgeView, BrandPenetrationGenderView,
} from './views/PenetrationViews';
import TrendsNationView from './views/TrendsNationView';
import TrendsAgeView from './views/TrendsAgeView';
import TrendsGenderView from './views/TrendsGenderView';
import TrendsIntensityView from './views/TrendsIntensityView';
import TrendsEngagementView from './views/TrendsEngagementView';
import TrendsGeoView from './views/TrendsGeoView';
import DoubleJeopardyView from './views/DoubleJeopardyView';
import NationalBrandShareView from './views/NationalBrandShareView';
import IntensityBrandShareView from './views/IntensityBrandShareView';
import LoyaltyBrandShareView from './views/LoyaltyBrandShareView';
import CategoryComparisonNationalView from './views/CategoryComparisonNationalView';
import CategoryComparisonProvincesView from './views/CategoryComparisonProvincesView';
import CategoryHmlProfileView from './views/CategoryHmlProfileView';
import { ByAgeView, ByGenderView, ByGeoView, IntensityView, LoyaltyView, CompetitionView, ShareOfRequirementsView, SocialView, OpportunityIndexByChannelView } from './views/AllViews';
import CountryView from './views/CountryView';
import ThailandDashboard from './views/thailand/ThailandDashboard';
import DocsView from './views/DocsView';
import DataSchemaView from './views/DataSchemaView';
import ValidationView from './views/ValidationView';
import { CategoryTargetAudienceView, BrandTargetAudienceView, PenetrationTargetsView } from './views/GrowthViews';
import LocationIntelligenceView from './views/LocationIntelligenceView';
import FootballAffinityView from './views/FootballAffinityView';
import ThemeParkAffinityView from './views/ThemeParkAffinityView';
import PinpointIntelligenceView from './views/PinpointIntelligenceView';
import MediaBudgetView from './views/MediaBudgetView';
import PanelSizeView from './views/PanelSizeView';
import PanelRepresentativenessView from './views/PanelRepresentativenessView';
import { Admin } from './utils/api';
import ChatWidget from './components/ChatWidget';
import './index.css';

const VIEW_TITLES = {
  'category-behaviour': 'Category Behaviour',
  'category-comparison-national':  'Category Comparison — National',
  'category-comparison-provinces': 'Category Comparison — Provinces',
  'category-hml-profile': 'Category H/M/L Profile',
  'penetration-geo':    'Category Penetration — By Geo',
  'penetration-age':    'Category Penetration — By Age Band',
  'penetration-gender': 'Category Penetration — By Gender',
  'brand-penetration-geo':    'Brand Penetration — By Geo',
  'brand-penetration-age':    'Brand Penetration — By Age Band',
  'brand-penetration-gender': 'Brand Penetration — By Gender',
  'panel-size':               'Panel Size Statistics',
  'panel-representativeness': 'Panel Representativeness',
  'double-jeopardy': 'Share and Penetration',
  'brand-share-national':  'Brand Share — National',
  'brand-share-intensity': 'Brand Share — H/M/L (Intensity)',
  'brand-share-loyalty':   'Brand Share — Solus/Dual (Loyalty)',
  'trends-nation':    'Monthly Trends — Nation Topline',
  'trends-age':       'Monthly Trends — By Age Band',
  'trends-gender':    'Monthly Trends — By Gender',
  'trends-geo':       'Monthly Trends — By Geography',
  'trends-intensity': 'Monthly Trends — Intensity',
  'trends-engagement': 'Monthly Trends — Engagement (App Loyalty)',
  'by-age':          'Customer Profiles — Demographics by Age',
  'by-gender':       'Customer Profiles — Demographics by Gender',
  'by-geo':          'Customer Profiles — Geography',
  intensity:         'Customer Profiles — Intensity',
  loyalty:           'App Loyalty',
  competition:       'Shared Customers — Duplication Matrix',
  sor:               'Shared Customers — Share of Requirements',
  social:                   'Identify Channel Preferences — Social Channel Reach',
  'channel-opp-index':      'Identify Channel Preferences — Opportunity Index by Channel',
  'category-target-audience': 'Growth — Who are the Category Target Audience?',
  'brand-target-audience':    'Growth — Who are my Brand Target Audience?',
  'penetration-targets':      'Growth — Penetration Targets',
  'location-map':             'Location Intelligence — Postcode Intelligence',
  'pinpoint-intelligence':    'Location Intelligence — Pinpoint Intelligence',
  'football-affinity':        'Omni Cross Category — Football Affinity',
  'theme-park-affinity':      'Omni Cross Category — Theme Parks Affinity',
  'media-budget-brand':       'Media Budget Allocation — Based on Brand Behavior',
  'media-budget-intensity':   'Media Budget Allocation — Based on Category Intensity',
  'data-schema':              'Data Schema',
};

function BlankState() {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      height: '100%', gap: 16, color: 'var(--text-muted)',
      padding: 40, textAlign: 'center',
    }}>
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" opacity="0.3">
        <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
        <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
      </svg>
      <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-secondary)' }}>
        Select a category to begin
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-muted)', maxWidth: 320, lineHeight: 1.6 }}>
        Use the category selector in the left panel to choose a market category.
        Your dashboard will populate with live BigQuery data.
      </div>
    </div>
  );
}

// Global month filter — lives in the topbar so it applies to every view.
// Defaults to the latest month available in the data.
function GlobalMonthFilter() {
  const { months, latestMonth, loading } = useMetaData();
  const { month, setMonth } = useMonth();

  if (loading || !months.length) return null;

  return (
    <>
      <span className="filter-label">Month</span>
      <select
        className="filter-select"
        value={month || latestMonth}
        onChange={e => setMonth(e.target.value)}
      >
        {months.map(m => <option key={m} value={m}>{fmtMonth(m)}</option>)}
      </select>
    </>
  );
}

function ForcePasswordChange() {
  const { completePasswordChange, logout } = useAuth();
  const [currentPw, setCurrentPw] = useState('');
  const [pw,        setPw]        = useState('');
  const [pw2,       setPw2]       = useState('');
  const [err,       setErr]       = useState('');
  const [busy,      setBusy]      = useState(false);

  const inputSt = {
    width: '100%', marginBottom: 10, padding: '10px 12px', borderRadius: 8,
    border: '1px solid var(--border)', background: 'var(--bg-base)',
    color: 'var(--text-primary)', fontSize: 14, boxSizing: 'border-box',
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentPw)        { setErr('Enter your current password.'); return; }
    if (pw.length < 8)     { setErr('New password must be at least 8 characters.'); return; }
    if (pw !== pw2)        { setErr('Passwords do not match.'); return; }
    setBusy(true); setErr('');
    try {
      await completePasswordChange(currentPw, pw);
    } catch (ex) {
      const msg = ex.code === 'auth/wrong-password' || ex.code === 'auth/invalid-credential'
        ? 'Current password is incorrect.'
        : ex.message || 'Failed to update password. Please try again.';
      setErr(msg);
      setBusy(false);
    }
  };

  return (
    <div style={{
      height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg-base)',
    }}>
      <div style={{
        background: 'var(--bg-card)', borderRadius: 12, padding: '40px 36px',
        width: 360, boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 6, color: 'var(--text-primary)' }}>
          Set a new password
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 24 }}>
          Your account requires a password change before you can continue.
        </div>
        <form onSubmit={handleSubmit}>
          <input
            type="password" placeholder="Current password" value={currentPw}
            onChange={e => setCurrentPw(e.target.value)} required
            style={inputSt}
          />
          <input
            type="password" placeholder="New password" value={pw}
            onChange={e => setPw(e.target.value)} required
            style={inputSt}
          />
          <input
            type="password" placeholder="Confirm new password" value={pw2}
            onChange={e => setPw2(e.target.value)} required
            style={{ ...inputSt, marginBottom: 16 }}
          />
          {err && <div style={{ color: 'var(--color-negative)', fontSize: 13, marginBottom: 12 }}>{err}</div>}
          <button type="submit" disabled={busy} style={{
            width: '100%', padding: '11px', borderRadius: 8, border: 'none',
            background: 'var(--teal)', color: '#fff', fontWeight: 600,
            fontSize: 14, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.7 : 1,
          }}>
            {busy ? 'Saving…' : 'Confirm'}
          </button>
        </form>
        <button onClick={logout} style={{
          width: '100%', marginTop: 10, padding: '9px', borderRadius: 8,
          border: '1px solid var(--border)', background: 'transparent',
          color: 'var(--text-muted)', fontSize: 13, cursor: 'pointer',
        }}>
          Sign out
        </button>
      </div>
    </div>
  );
}

function DashboardApp({ country, setCountry, showLanding, setShowLanding, scrollToBook, setScrollToBook }) {
  const { user, loading, isEurekaUser, mustChangePassword, allowedCountries } = useAuth();
  const { dark, toggle }         = useContext(ThemeContext);
  const { category }             = useCategory();
  const { month }                = useMonth();
  const [view,        setView]   = useState(() => readUrlState().view || 'panel-representativeness');
  const [cacheMsg,    setCacheMsg] = useState('');

  // Keep the URL in sync with market + category + month + view so the view is
  // deep-linkable (Duplicate Tab / bookmark / shared link reopen it). Only once
  // we're actually in the dashboard (past landing + country gates).
  const inDashboard = !!user && !showLanding && !!country;
  useEffect(() => {
    if (inDashboard) writeUrlState({ country, category, month, view });
  }, [inDashboard, country, category, month, view]);

  if (loading) {
    return <div className="loading-state" style={{ height: '100vh' }}><div className="spinner" /><span>Loading…</span></div>;
  }

  if (!user) return <LoginPage />;
  if (mustChangePassword) return <ForcePasswordChange />;

  // /admin — access management, Eureka team only (no client-side router;
  // Firebase Hosting rewrites all paths to index.html)
  if (window.location.pathname === '/admin') {
    if (isEurekaUser) return <AdminView />;
    window.location.replace('/');
    return null;
  }
  if (showLanding) return <LandingView onEnter={() => setShowLanding(false)} scrollToBook={scrollToBook} onScrolled={() => setScrollToBook(false)} />;
  // Country-level access: fall back to the selector if the chosen market
  // isn't permitted (covers stale state and permission changes mid-session)
  if (!country || !isCountryAllowed(allowedCountries, country)) {
    return <CountryView onSelect={setCountry} onHome={() => { setShowLanding(true); setScrollToBook(true); }} />;
  }
  if (country === 'th') return <ThailandDashboard onBack={() => setCountry(null)} />;

  const handleClearCache = async () => {
    try {
      await Admin.invalidateCache();
      setCacheMsg('Cache cleared ✓');
      setTimeout(() => setCacheMsg(''), 3000);
    } catch { setCacheMsg('Failed'); }
  };

  const renderView = () => {
    // Eureka team only; available without a category
    if (view === 'docs')        return isEurekaUser ? <DocsView />        : <BlankState />;
    if (view === 'data-schema') return isEurekaUser ? <DataSchemaView />  : <BlankState />;
    if (view === 'validation')  return isEurekaUser ? <ValidationView />  : <BlankState />;
    if (view === 'panel-representativeness') return <PanelRepresentativenessView />;
    if (view === 'football-affinity')        return <FootballAffinityView />;
    if (view === 'theme-park-affinity')      return <ThemeParkAffinityView />;
    if (view === 'pinpoint-intelligence')    return <PinpointIntelligenceView />;
    if (view === 'media-budget-brand')       return <MediaBudgetView mode="brand" />;
    if (view === 'media-budget-intensity')   return <MediaBudgetView mode="intensity" />;
    if (view === 'category-comparison-national')  return <CategoryComparisonNationalView />;
    if (view === 'category-comparison-provinces') return <CategoryComparisonProvincesView />;
    if (!category) return <BlankState />;
    switch (view) {
      case 'category-behaviour': return <CategoryBehaviourView />;
      case 'category-hml-profile': return <CategoryHmlProfileView />;
      case 'penetration-geo':    return <PenetrationGeoView />;
      case 'penetration-age':    return <PenetrationAgeView />;
      case 'penetration-gender': return <PenetrationGenderView />;
      case 'brand-penetration-geo':    return <BrandPenetrationGeoView />;
      case 'brand-penetration-age':    return <BrandPenetrationAgeView />;
      case 'brand-penetration-gender': return <BrandPenetrationGenderView />;
      case 'panel-size': return <PanelSizeView />;
      case 'double-jeopardy': return <DoubleJeopardyView />;
      case 'brand-share-national':  return <NationalBrandShareView />;
      case 'brand-share-intensity': return <IntensityBrandShareView />;
      case 'brand-share-loyalty':   return <LoyaltyBrandShareView />;
      case 'trends-nation':   return <TrendsNationView />;
      case 'trends-age':      return <TrendsAgeView />;
      case 'trends-gender':    return <TrendsGenderView />;
      case 'trends-geo':       return <TrendsGeoView />;
      case 'trends-intensity': return <TrendsIntensityView />;
      case 'trends-engagement': return <TrendsEngagementView />;
      case 'by-age':           return <ByAgeView />;
      case 'by-gender':        return <ByGenderView />;
      case 'by-geo':           return <ByGeoView />;
      case 'intensity':        return <IntensityView />;
      case 'loyalty':         return <LoyaltyView />;
      case 'competition':     return <CompetitionView />;
      case 'sor':             return <ShareOfRequirementsView />;
      case 'social':                    return <SocialView />;
      case 'channel-opp-index':         return <OpportunityIndexByChannelView />;
      case 'category-target-audience': return <CategoryTargetAudienceView />;
      case 'brand-target-audience':    return <BrandTargetAudienceView />;
      case 'penetration-targets':      return <PenetrationTargetsView />;
      case 'location-map':             return <LocationIntelligenceView />;
      default:                         return <BlankState />;
    }
  };

  const title = view === 'docs'       && isEurekaUser ? 'Documentation'
    : view === 'validation'  && isEurekaUser ? 'Data Validation'
    : view === 'data-schema' && isEurekaUser ? 'Data Schema'
    : view === 'football-affinity' || view === 'theme-park-affinity' || view === 'pinpoint-intelligence' || view === 'media-budget-brand' || view === 'media-budget-intensity' || view === 'category-comparison-national' || view === 'category-comparison-provinces' ? (VIEW_TITLES[view] || view)
    : category
      ? `${category} · ${VIEW_TITLES[view] || view}`
      : 'Select a category';

  return (
    <div className="app-shell">
      <Sidebar activeView={view} onNavigate={setView} onGoHome={() => setCountry(null)} country={country} />
      <div className="main-content">
        <div className="topbar">
          <div className="topbar-title">{title}</div>
          <div className="topbar-spacer" />
          {category && <GlobalMonthFilter />}
          {cacheMsg && <span className="topbar-badge">{cacheMsg}</span>}
          <button
            title="Change market"
            onClick={() => setCountry(null)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontSize: 12, fontWeight: 600,
              color: 'var(--teal)',
              background: 'var(--teal-light)',
              border: '1px solid rgba(29,184,154,0.30)',
              borderRadius: 100, padding: '4px 12px 4px 8px',
              cursor: 'pointer', letterSpacing: '0.01em',
            }}
          >
            <span style={{ fontSize: 15, lineHeight: 1 }}>
              {{'uk':'🇬🇧','us':'🇺🇸','th':'🇹🇭','ksa':'🇸🇦'}[country] || '🌐'}
            </span>
            {{'uk':'United Kingdom','us':'United States','th':'Thailand','ksa':'Saudi Arabia'}[country] || country}
          </button>
          <button className="btn-icon" title="Clear server cache" onClick={handleClearCache}>↺</button>
          <button className="btn-icon" title={dark ? 'Light mode' : 'Dark mode'} onClick={toggle}>
            {dark ? '☀️' : '🌙'}
          </button>
          <button
            className="btn-home"
            title="Back to country select"
            onClick={() => setCountry(null)}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
            Back
          </button>
        </div>
        <div className="page-body">{renderView()}</div>
      </div>
      <ChatWidget view={view} pageLabel={VIEW_TITLES[view] || view} category={category} month={month} />
    </div>
  );
}

export default function App() {
  // Restore market from a deep link (e.g. a duplicated tab) and skip the landing
  // page so it lands straight on the requested view.
  const [country,      setCountry]      = useState(() => readUrlState().country);
  const [showLanding,  setShowLanding]  = useState(() => !hasDeepLink());
  const [scrollToBook, setScrollToBook] = useState(false);

  return (
    <ThemeProvider>
      <AuthProvider>
        <CategoryProvider>
          <MonthProvider>
            <DashboardApp
              country={country}           setCountry={setCountry}
              showLanding={showLanding}   setShowLanding={setShowLanding}
              scrollToBook={scrollToBook} setScrollToBook={setScrollToBook}
            />
          </MonthProvider>
        </CategoryProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
